import React, { useEffect, useRef } from 'react';
import { IframeResizeDetectorService } from '../../lib/helpers/iframe-resize-detector';
import './IframeResizeDetector.scss';

interface IframeResizeDetectorProps {
  children?: React.ReactNode;
}

/**
 * Iframe Resize Detection Component
 * 
 * Wraps content with hidden elements for ResizeObserver and iframe fallback
 * to detect content size changes when Passport is embedded.
 */
export const IframeResizeDetector: React.FunctionComponent<IframeResizeDetectorProps> = ({ children }): JSX.Element => {
  const detectorRef = useRef<IframeResizeDetectorService | null>(null);
  const sizeDetectorRef = useRef<HTMLDivElement>(null);
  const iframeFallbackRef = useRef<HTMLIFrameElement>(null);
  const [isEmbedded, setIsEmbedded] = React.useState(false);

  useEffect(() => {
    detectorRef.current = new IframeResizeDetectorService();
    const embedded = detectorRef.current.getIsEmbedded();
    setIsEmbedded(embedded);
    if (!embedded) {
      return;
    }

    detectorRef.current.init();
    
    setTimeout(() => {
      if (detectorRef.current && sizeDetectorRef.current) {
        detectorRef.current.attachObserver(sizeDetectorRef.current);
      }

      if (detectorRef.current && iframeFallbackRef.current) {
        detectorRef.current.attachIframeFallback(iframeFallbackRef.current);
      }

      if (detectorRef.current) {
        detectorRef.current.resizeHandler();
      }
    }, 100);

    return () => {
      if (detectorRef.current) {
        detectorRef.current.destroy();
      }
    };
  }, []);

  return (
    <div id="iframe-size-detector-wrapper" style={{ position: 'relative', width: '100%', height: '100%' }}>
      {children}
      
      {isEmbedded && (
        <>
          <iframe 
            id="size-detector-fallback"
            ref={iframeFallbackRef}
            title="Size detector fallback"
          />

          <div 
            id="size-detector"
            ref={sizeDetectorRef}
          />
        </>
      )}
    </div>
  );
};

export default IframeResizeDetector;
